/**
 * Smooth scroll utility using smoothscroll-polyfill
 * Provides consistent smooth scrolling behavior across all browsers
 */

/**
 * Smooth scroll to a specific position
 * @param {HTMLElement} element - The element to scroll
 * @param {Object} options - Scroll options
 * @param {number} options.top - Top position
 * @param {number} options.left - Left position
 * @param {string} options.behavior - Scroll behavior ('smooth' or 'auto')
 */
export const smoothScrollTo = (element, options = {}) => {
  if (!element) return
  
  const defaultOptions = {
    top: 0,
    left: 0,
    behavior: 'smooth'
  }
  
  const scrollOptions = { ...defaultOptions, ...options }
  
  if (element.scrollTo) {
    element.scrollTo(scrollOptions)
  } else {
    // Fallback for older browsers
    element.scrollTop = scrollOptions.top
    element.scrollLeft = scrollOptions.left
  }
}

/**
 * Smooth scroll by a specific amount
 * @param {HTMLElement} element - The element to scroll
 * @param {Object} options - Scroll options
 * @param {number} options.top - Top offset
 * @param {number} options.left - Left offset
 * @param {string} options.behavior - Scroll behavior ('smooth' or 'auto')
 */
export const smoothScrollBy = (element, options = {}) => {
  if (!element) return
  
  const defaultOptions = {
    top: 0,
    left: 0,
    behavior: 'smooth'
  }
  
  const scrollOptions = { ...defaultOptions, ...options }
  
  if (element.scrollBy) {
    element.scrollBy(scrollOptions)
  } else {
    // Fallback for older browsers
    element.scrollTop += scrollOptions.top
    element.scrollLeft += scrollOptions.left
  }
}

/**
 * Smooth scroll element into view
 * @param {HTMLElement} element - The element to scroll into view
 * @param {Object} options - Scroll options
 * @param {string} options.behavior - Scroll behavior ('smooth' or 'auto')
 * @param {string} options.block - Vertical alignment ('start', 'center', 'end', 'nearest')
 * @param {string} options.inline - Horizontal alignment ('start', 'center', 'end', 'nearest')
 */
export const smoothScrollIntoView = (element, options = {}) => {
  if (!element) return
  
  const defaultOptions = {
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest'
  }
  
  const scrollOptions = { ...defaultOptions, ...options }
  
  if (element.scrollIntoView) {
    element.scrollIntoView(scrollOptions)
  }
}

/**
 * Smooth scroll window to a specific position
 * @param {Object} options - Scroll options
 * @param {number} options.top - Top position
 * @param {number} options.left - Left position
 * @param {string} options.behavior - Scroll behavior ('smooth' or 'auto')
 */
export const smoothScrollWindowTo = (options = {}) => {
  const defaultOptions = {
    top: 0,
    left: 0,
    behavior: 'smooth'
  }
  
  const scrollOptions = { ...defaultOptions, ...options }
  
  if (window.scrollTo) {
    window.scrollTo(scrollOptions)
  } else {
    // Fallback for older browsers
    window.scrollTo(scrollOptions.left, scrollOptions.top)
  }
}

/**
 * Smooth scroll window by a specific amount
 * @param {Object} options - Scroll options
 * @param {number} options.top - Top offset
 * @param {number} options.left - Left offset
 * @param {string} options.behavior - Scroll behavior ('smooth' or 'auto')
 */
export const smoothScrollWindowBy = (options = {}) => {
  const defaultOptions = {
    top: 0,
    left: 0,
    behavior: 'smooth'
  }
  
  const scrollOptions = { ...defaultOptions, ...options }
  
  if (window.scrollBy) {
    window.scrollBy(scrollOptions)
  } else {
    // Fallback for older browsers
    window.scrollBy(scrollOptions.left, scrollOptions.top)
  }
} 